import { TimeInput as MantineTimePicker, TimeInputProps } from '@mantine/dates';
import { forwardRef } from 'react';
import { useMantineTheme } from '@mantine/core';
import { usePreferencesStore } from '../../../stores';
import { defaultColorScheme } from '../../../theme/theme';

export type GradientVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger';

export interface ThemedTimePickerProps extends TimeInputProps {
  gradientFocus?: boolean;
  gradientVariant?: GradientVariant;
  useColorScheme?: boolean; // Use color scheme from state management
}

const TimePicker = forwardRef<HTMLInputElement, ThemedTimePickerProps>(
  ({ gradientFocus = true, gradientVariant = 'primary', useColorScheme = true, style, styles, ...rest }, ref) => {
    const theme = useMantineTheme();
    const colorSchemeKey = usePreferencesStore((state) => state.colorSchemeKey);
    const reducedMotion = usePreferencesStore((state) => state.reducedMotion);
    const strongFocus = usePreferencesStore((state) => state.strongFocus);

    // Determine which color to use
    let color = theme.other.gradients[gradientVariant].from;

    if (useColorScheme) {
      const effectiveColorSchemeKey = colorSchemeKey || defaultColorScheme.key;
      const colorScheme = theme.other.colorSchemes?.find((cs: any) => cs.key === effectiveColorSchemeKey);
      if (colorScheme) {
        color = colorScheme.primary.from;
      }
    }

    if (!gradientFocus) {
      return (
        <MantineTimePicker
          ref={ref}
          style={style}
          styles={{
            ...styles,
            label: {
              textAlign: 'left !important' as any,
              ...(styles as any)?.label,
            },
            input: {
              paddingLeft: rest.leftSection ? '40px' : undefined,
              paddingRight: rest.rightSection ? '40px' : undefined,
              ...(styles as any)?.input,
            },
          }}
          {...rest}
        />
      );
    }

    return (
      <MantineTimePicker
        ref={ref}
        style={style}
        styles={{
          ...styles,
          label: {
            textAlign: 'left !important' as any,
            ...(styles as any)?.label,
          },
          input: {
            paddingLeft: rest.leftSection ? '40px' : undefined,
            paddingRight: rest.rightSection ? '40px' : undefined,
            transition: reducedMotion ? 'none' : 'border-color 0.2s ease, box-shadow 0.2s ease',
            ...(styles as any)?.input,
          },
        }}
        // Use Mantine's proper styles API for pseudo-selectors
        sx={(theme) => ({
          input: {
            '&:focus': {
              borderColor: `${color} !important`,
              boxShadow: strongFocus
                ? `0 0 0 3px ${color}50 !important`
                : `0 0 0 2px ${color}30 !important`,
              outline: strongFocus ? `2px solid ${color} !important` : 'none',
              outlineOffset: strongFocus ? '2px' : '0',
            },
            '&:focus-within': {
              borderColor: `${color} !important`,
            },
          },
        })}
        {...rest}
      />
    );
  }
);

TimePicker.displayName = 'TimePicker';

export default TimePicker;
